import {
  REALTIME_VOICE_WARM_QUERY,
  type RealtimeAgentState,
  type RealtimeClientEvent,
  type RealtimeServerEvent,
} from '@wundo/shared/realtimeVoice.ts';
import { RealtimeHandover } from '@wundo/shared/realtimeHandover.ts';
import { PCM_SAMPLE_RATE } from './createPcmCapture.ts';
import { hangUpVoiceSocket } from './hangUpVoiceSocket.ts';
import { RealtimeAudioGraph } from './realtimeAudioGraph.ts';
import type {
  RealtimeCaptureMessage,
  RealtimePlaybackReport,
} from './realtimeAudioMessages.ts';
import { RealtimeCaption } from './realtimeCaption.ts';
import type { PlaybackGapSummary } from './realtimePlaybackGaps.ts';

const INITIAL_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 10_000;

/**
 * Refusals: the session is not this reader's, or a second tab has taken the
 * call over. Neither is answered by connecting again.
 */
const REFUSED_CLOSE_CODES = [4403, 4409];

/** How much a reading has to move before the orb is asked to redraw. */
const LEVEL_STEP = 0.02;

/** What the character is doing, plus the state of having no answer yet. */
export type RealtimeVoiceState = RealtimeAgentState | 'connecting';

export interface RealtimeVoiceListener {
  onState: (state: RealtimeVoiceState) => void;
  onLevel: (level: number) => void;
  /** What the character has said so far, as far as its voice has played. */
  onCaption: (text: string) => void;
  onSuggestEnd: () => void;
  /** The character said goodbye and hung up, and its last words have played. */
  onEnded: () => void;
  /** The character's voice failed on one answer; the call goes on. */
  onVoiceTrouble: () => void;
  onError: (message: string) => void;
  /** The call is over and is not coming back on its own. */
  onClosed: () => void;
}

export interface RealtimeVoiceOptions {
  /** The bridge's WebSocket address, as the start of the session handed it out. */
  url: string;
  microphoneId: string | null;
  speakerId: string | null;
}

/**
 * A spoken practice conversation over one socket to our API.
 *
 * There is no peer connection and no room: the microphone goes up as PCM and
 * the character's voice comes back as PCM, both over the same socket, and
 * everything either side knows about the conversation travels beside them as
 * JSON. Audio is converted on the audio thread — see the two worklets — so a
 * busy page cannot stutter the call.
 *
 * A dropped line is not a dropped conversation. The socket reconnects on its
 * own and the bridge builds the model's session back from what was said, so the
 * microphone, the speaker and the audio graph are set up once and survive it.
 */
export class RealtimeVoiceClient {
  private readonly url: string;
  private readonly listener: RealtimeVoiceListener;
  private readonly caption: RealtimeCaption;
  private readonly handover: RealtimeHandover;
  private readonly audio: RealtimeAudioGraph;

  private socket: WebSocket | null = null;
  /** The second socket, on a newer task, until it takes the call over. */
  private warmSocket: WebSocket | null = null;
  /** The socket that was authoritative until the swap, waiting to be closed. */
  private retiringSocket: WebSocket | null = null;

  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private attempts = 0;
  private stopped = false;
  private failed = false;
  private ready = false;
  private warmIsReady = false;
  private sentChunks = 0;
  private drainedChunks = 0;
  /**
   * The bridge says `listening` once it has sent the last audio, which is
   * seconds before it has played; the screen waits for the voice to finish.
   */
  private listeningAfterPlayback = false;
  /** The character hung up; the call ends once its goodbye has played. */
  private endedByCharacter = false;
  private level = 0;

  constructor(options: RealtimeVoiceOptions, listener: RealtimeVoiceListener) {
    this.url = options.url;
    this.listener = listener;
    this.audio = new RealtimeAudioGraph({
      microphoneId: options.microphoneId,
      speakerId: options.speakerId,
      onCapture: (message) => this.sendAudio(message),
      onPlayback: (report) => this.handlePlayback(report),
    });
    this.caption = new RealtimeCaption((text) => listener.onCaption(text));
    this.handover = new RealtimeHandover({
      openWarm: () => this.openWarmSocket(),
      replay: (chunks) => this.replayToWarm(chunks),
      sendHandover: () => this.send(this.socket, { type: 'handover' }),
      sendPromote: (cutBytes) => this.promoteWarmSocket(cutBytes),
      closeOld: () => this.closeRetiringSocket(),
    });
  }

  /**
   * Take the microphone, build the audio graph and open the socket.
   *
   * Rejects when the microphone is refused, which is a different failure from a
   * call that cannot connect — the screen says so.
   */
  async connect(): Promise<void> {
    this.listener.onState('connecting');
    await this.audio.open();

    /* Hung up while the browser was still handing the microphone over. */
    if (this.stopped) {
      await this.disconnect();
      return;
    }

    this.openSocket();
  }

  /** Muted here rather than at the bridge: nothing should leave the machine. */
  setMicrophoneEnabled(enabled: boolean): void {
    this.audio.setMicrophoneEnabled(enabled);
  }

  /** Switch microphones mid-conversation, without touching the call itself. */
  async setMicrophone(deviceId: string): Promise<void> {
    await this.audio.setMicrophone(deviceId);
  }

  async setSpeaker(deviceId: string): Promise<void> {
    await this.audio.setSpeaker(deviceId);
  }

  /** Hang up for good: nothing here reconnects afterwards. */
  async disconnect(): Promise<void> {
    this.stopped = true;
    this.ready = false;
    this.warmIsReady = false;
    this.handover.dispose();

    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    const socket = this.socket;
    this.socket = null;

    const warmSocket = this.warmSocket;
    this.warmSocket = null;
    warmSocket?.close(1_000);

    this.audio.release();
    await hangUpVoiceSocket(socket);
    await this.audio.close();
  }

  private openSocket(): void {
    /* A fresh line to the call starts no handover from where the last one stood. */
    this.abandonWarmSocket();

    const socket = new WebSocket(this.url);
    socket.binaryType = 'arraybuffer';
    this.socket = socket;
    this.handover.liveOpened();

    socket.onopen = () => {
      this.attempts = 0;
    };

    this.wireLiveSocket(socket);
  }

  /** What the authoritative socket does, whether it was opened here or promoted. */
  private wireLiveSocket(socket: WebSocket): void {
    socket.onmessage = (event: MessageEvent<unknown>) => {
      this.receive(event.data);
    };

    socket.onclose = (event) => {
      /* A socket we have already replaced has nothing left to say. */
      if (this.socket !== socket) {
        return;
      }

      this.socket = null;

      /* With a warm socket ready this is the switch, not a dropped call. */
      if (event.code !== 1_000 && this.handover.canPromote) {
        this.handover.oldSocketLost();
        return;
      }

      this.handleClose(event.code);
    };
  }

  /**
   * A second socket that lands on a newer task and hears the microphone too.
   *
   * It carries no audio to the speaker and writes nothing down until it is
   * promoted, so the call underneath it never notices that it exists.
   */
  private openWarmSocket(): void {
    if (this.stopped) {
      return;
    }

    const separator = this.url.includes('?') ? '&' : '?';
    const socket = new WebSocket(`${this.url}${separator}${REALTIME_VOICE_WARM_QUERY}`);
    socket.binaryType = 'arraybuffer';
    this.warmSocket = socket;
    this.warmIsReady = false;

    socket.onopen = () => {
      this.handover.warmOpened();
    };

    socket.onmessage = (event: MessageEvent<unknown>) => {
      this.receiveWarm(event.data);
    };

    socket.onclose = () => {
      if (this.warmSocket !== socket) {
        return;
      }

      this.warmSocket = null;
      this.warmIsReady = false;
      /* Refused by an equally old task or simply dropped: try again in a moment. */
      this.handover.warmRefused();
    };
  }

  private abandonWarmSocket(): void {
    const warmSocket = this.warmSocket;
    this.warmSocket = null;
    this.warmIsReady = false;
    warmSocket?.close(1_000);
  }

  /** Nothing a warm socket says is believed, and nothing it sends is heard. */
  private receiveWarm(data: unknown): void {
    if (typeof data !== 'string') {
      return;
    }

    let event: RealtimeServerEvent;
    try {
      event = JSON.parse(data) as RealtimeServerEvent;
    } catch {
      return;
    }

    if (event.type === 'ready') {
      this.warmIsReady = true;
      this.handover.warmReady();
      return;
    }

    /* A warm socket that cannot start is no use; another task may do better. */
    if (event.type === 'error') {
      this.warmSocket?.close(1_000);
    }
  }

  private replayToWarm(chunks: Uint8Array<ArrayBuffer>[]): void {
    const warmSocket = this.warmSocket;
    if (warmSocket?.readyState !== WebSocket.OPEN) {
      return;
    }

    chunks.forEach((chunk) => warmSocket.send(chunk));
  }

  /**
   * The warm socket becomes the call, without a break anybody can hear: the
   * playback queue keeps draining and the screen is never told to wait.
   */
  private promoteWarmSocket(cutBytes: number | undefined): void {
    const warmSocket = this.warmSocket;
    if (warmSocket === null) {
      return;
    }

    this.send(warmSocket, { type: 'promote', cut_bytes: cutBytes });

    this.retiringSocket = this.socket;
    this.socket = warmSocket;
    this.warmSocket = null;
    this.warmIsReady = false;
    this.attempts = 0;
    this.wireLiveSocket(warmSocket);
  }

  private closeRetiringSocket(): void {
    const socket = this.retiringSocket;
    this.retiringSocket = null;
    if (socket === null) {
      return;
    }

    /* Left wired, its close would be read as a call that needs reconnecting. */
    socket.onmessage = null;
    socket.onclose = null;
    socket.close(1_000);
  }

  private receive(data: unknown): void {
    if (data instanceof ArrayBuffer) {
      this.play(data);
      return;
    }

    if (typeof data !== 'string') {
      return;
    }

    let event: RealtimeServerEvent;
    try {
      event = JSON.parse(data) as RealtimeServerEvent;
    } catch {
      /* Anything that is not a shape we know is ignored rather than guessed at. */
      return;
    }

    this.handleEvent(event);
  }

  private handleEvent(event: RealtimeServerEvent): void {
    switch (event.type) {
      case 'ready':
        this.ready = true;
        this.listeningAfterPlayback = false;
        this.listener.onState('listening');
        return;
      case 'agent_state':
        this.handleAgentState(event.state, event.item_id);
        return;
      case 'interrupt':
        this.clearPlayback();
        this.settleListening();
        return;
      case 'transcript_delta':
        this.caption.addText(event.item_id, event.delta);
        return;
      case 'transcript':
        /* The caption is built from the deltas, in step with the voice. */
        return;
      case 'turn_end':
        this.handover.turnEnded(event.since_bytes, event.end_bytes);
        return;
      case 'prepare_reconnect':
        this.handover.prepareRequested();
        return;
      case 'settled':
        this.handover.settledBefore(event.since_bytes);
        return;
      case 'released':
        this.handover.released(event.cut_bytes);
        return;
      case 'suggest_end':
        this.listener.onSuggestEnd();
        return;
      case 'ended':
        this.endedByCharacter = true;
        this.settleEnded();
        return;
      case 'tts_error':
        this.listener.onVoiceTrouble();
        return;
      case 'reconnect':
        /* With a warm socket ready, the old task going away is the switch. */
        if (this.handover.canPromote) {
          this.handover.oldSocketLost();
          return;
        }
        this.reopen();
        return;
      case 'error':
        this.failed = true;
        this.listener.onError(event.message);
        return;
    }
  }

  private handleAgentState(state: RealtimeAgentState, itemId: string | undefined): void {
    if (state === 'speaking') {
      const mark = this.caption.startAnswer(itemId ?? '');
      this.audio.command({ type: 'mark', mark, itemId: itemId ?? '' });
    }

    /* The bridge says `listening` once an answer is done, so no more of its audio is coming. */
    if (state === 'listening') {
      this.audio.command({ type: 'end' });
      this.caption.complete();
      this.listeningAfterPlayback = true;
      this.settleListening();
      return;
    }

    this.listeningAfterPlayback = false;
    this.listener.onState(state);
  }

  private settleListening(): void {
    if (!this.listeningAfterPlayback || this.drainedChunks !== this.sentChunks) {
      return;
    }

    this.listeningAfterPlayback = false;
    this.listener.onState('listening');
  }

  private settleEnded(): void {
    if (!this.endedByCharacter || this.drainedChunks !== this.sentChunks) {
      return;
    }

    this.endedByCharacter = false;
    this.listener.onEnded();
  }

  private clearPlayback(): void {
    this.caption.stop();
    this.audio.command({ type: 'clear' });
    this.drainedChunks = this.sentChunks;
  }

  /** The API task is going away. Drop this socket and pick up a new one. */
  private reopen(): void {
    this.ready = false;
    this.listeningAfterPlayback = false;
    this.listener.onState('connecting');
    this.clearPlayback();

    const socket = this.socket;
    this.socket = null;
    socket?.close(1_000);

    this.scheduleReconnect();
  }

  private handleClose(code: number): void {
    this.ready = false;
    this.listeningAfterPlayback = false;

    if (this.stopped) {
      return;
    }

    if (this.failed || REFUSED_CLOSE_CODES.includes(code)) {
      if (!this.failed) {
        this.listener.onError(`Practice call refused (${code})`);
      }
      this.listener.onClosed();
      return;
    }

    /* A clean close from the bridge is the end of the conversation. */
    if (code === 1_000) {
      this.listener.onClosed();
      return;
    }

    this.listener.onState('connecting');
    this.clearPlayback();
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    this.attempts += 1;
    const delay = Math.min(
      INITIAL_RECONNECT_DELAY_MS * 2 ** (this.attempts - 1),
      MAX_RECONNECT_DELAY_MS,
    );

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.stopped) {
        this.openSocket();
      }
    }, delay);
  }

  private sendAudio(message: RealtimeCaptureMessage): void {
    if (message.level !== null) {
      this.reportLevel(message.level);
    }

    /* Before `ready` there is no model session on the other end to append to. */
    if (!this.ready || this.socket?.readyState !== WebSocket.OPEN) {
      return;
    }

    const pcm = message.pcm.buffer;
    this.socket.send(pcm);
    this.handover.audioChunk(new Uint8Array(pcm));

    if (this.warmIsReady && this.warmSocket?.readyState === WebSocket.OPEN) {
      this.warmSocket.send(pcm);
    }
  }

  private reportLevel(level: number): void {
    if (Math.abs(level - this.level) < LEVEL_STEP) {
      return;
    }

    this.level = level;
    this.listener.onLevel(level);
  }

  private play(data: ArrayBuffer): void {
    this.caption.addAudio(this.toMs(data.byteLength / Int16Array.BYTES_PER_ELEMENT));
    this.sentChunks += 1;
    this.audio.play(data);
  }

  private handlePlayback(report: RealtimePlaybackReport): void {
    if (report.type === 'progress') {
      this.caption.played(report.mark, this.toMs(report.samples));
      return;
    }

    if (report.type === 'drained') {
      this.drainedChunks = report.chunks;
      this.settleListening();
      this.settleEnded();
      return;
    }

    if (report.type === 'gaps') {
      this.reportGaps(report.summary);
      return;
    }

    this.reportPlayed(report.samples);
  }

  private toMs(samples: number): number {
    return (samples / PCM_SAMPLE_RATE) * 1_000;
  }

  /**
   * How much of the character's turn was actually heard.
   *
   * Milliseconds rather than a position in a turn: the bridge is the only side
   * that knows which item is speaking, and it counts from the moment it said so.
   */
  private reportPlayed(samples: number): void {
    this.send(this.socket, {
      type: 'played',
      item_id: '',
      ms: Math.round(this.toMs(samples)),
    });
  }

  private reportGaps(summary: PlaybackGapSummary): void {
    this.send(this.socket, {
      type: 'playback_gaps',
      item_id: summary.itemId,
      played_ms: summary.playedMs,
      gaps: summary.gaps,
      gap_ms: summary.gapMs,
      longest_gap_ms: summary.longestGapMs,
      offsets_ms: summary.offsetsMs,
      end: summary.end,
    });
  }

  private send(socket: WebSocket | null, event: RealtimeClientEvent): void {
    if (socket?.readyState !== WebSocket.OPEN) {
      return;
    }

    socket.send(JSON.stringify(event));
  }
}
